import { computed, ref, watch } from 'vue';
import type { Ref } from 'vue';
import axios from 'axios';
import type { AxiosProgressEvent } from 'axios';
import { useUserStore } from '@/entities/User';
import { positionApi } from '@/entities/Position';
import {
  TRAINING_CONTENT_MAX_VISIBILITY_IDS,
  TRAINING_CONTENT_TITLE_MAX_LENGTH,
  TRAINING_CONTENT_MAX_UPLOAD_SIZE_BYTES,
  TRAINING_CONTENT_MAX_UPLOAD_SIZE_LABEL,
  TRAINING_CONTENT_URL_MAX_LENGTH,
  trainingContentApi,
} from '@/entities/Training';
import type {
  CreateTrainingContentPayload,
  TrainingContentExtension,
  TrainingContentType,
} from '@/entities/Training';
import { extractApiErrorMessage, formatUrl, useCustomToast, validateUrl } from '@/shared/lib';

export type TrainingContentUploadStatus = 'idle' | 'uploading' | 'completed';

export interface UseCreateTrainingContentOptions {
  selectedPositionIds?: Ref<string[]>;
  selectedMemberIds?: Ref<string[]>;
}

export function useCreateTrainingContent(options: UseCreateTrainingContentOptions = {}) {
  const userStore = useUserStore();
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const contentType = ref<TrainingContentType | null>(null);
  const extension = ref<TrainingContentExtension | null>(null);
  const title = ref('');
  const selectedPositionId = ref<number | null>(null);
  const createdDate = ref<Date>(new Date());
  const file = ref<File | null>(null);
  const url = ref('');
  const fileError = ref('');
  const uploadProgress = ref(0);
  const uploadStatus = ref<TrainingContentUploadStatus>('idle');
  const isSubmitting = ref(false);

  let abortController: AbortController | null = null;

  const responsibleName = computed(() => userStore.name ?? '');
  const responsibleMemberId = computed(() => userStore.memberId);

  const initialPositionId = ref<number | null>(null);
  const availablePositions = ref<{ id: number; name: string }[]>([]);
  const isLoadingPositions = ref(false);

  const hasSinglePosition = computed(() => availablePositions.value.length === 1);

  const loadPositions = async () => {
    isLoadingPositions.value = true;
    try {
      const positions = await positionApi.getPositionsByToken();
      availablePositions.value = positions.map((position) => ({
        id: position.positionId,
        name: position.positionName,
      }));

      if (hasSinglePosition.value) {
        selectedPositionId.value = availablePositions.value[0].id;
        initialPositionId.value = availablePositions.value[0].id;
      }
    } catch {
      availablePositions.value = [];
      showErrorToast({ title: 'Erro ao carregar os cargos do usuário', type: 'error' });
    } finally {
      isLoadingPositions.value = false;
    }
  };

  const isUploadDisabled = computed(() => url.value.trim().length > 0 || isSubmitting.value);
  const isUrlDisabled = computed(() => file.value !== null || isSubmitting.value);

  const urlValidation = computed(() => validateUrl(url.value));
  const urlError = computed(() => {
    if (formatUrl(url.value).length > TRAINING_CONTENT_URL_MAX_LENGTH) {
      return `A URL deve ter no máximo ${TRAINING_CONTENT_URL_MAX_LENGTH} caracteres.`;
    }
    return urlValidation.value.error;
  });

  const selectFile = (candidate: File | null) => {
    fileError.value = '';

    if (!candidate) {
      return;
    }

    if (candidate.size > TRAINING_CONTENT_MAX_UPLOAD_SIZE_BYTES) {
      fileError.value = `O arquivo excede o tamanho máximo de ${TRAINING_CONTENT_MAX_UPLOAD_SIZE_LABEL}.`;
      return;
    }

    file.value = candidate;
    uploadProgress.value = 100;
    uploadStatus.value = 'completed';
  };

  const removeFile = () => {
    file.value = null;
    fileError.value = '';
    uploadProgress.value = 0;
    uploadStatus.value = 'idle';
  };

  watch(
    url,
    () => {
      if (!file.value) {
        fileError.value = '';
      }
    },
    { flush: 'sync' }
  );

  const selectedPositionIds = options.selectedPositionIds ?? ref<string[]>([]);
  const selectedMemberIds = options.selectedMemberIds ?? ref<string[]>([]);

  const hasContent = computed(() => file.value !== null || url.value.trim().length > 0);

  const visibilityError = computed(() =>
    selectedMemberIds.value.length > TRAINING_CONTENT_MAX_VISIBILITY_IDS
      ? `Selecione no máximo ${TRAINING_CONTENT_MAX_VISIBILITY_IDS} colaboradores.`
      : ''
  );

  const isDirty = computed(
    () =>
      contentType.value !== null ||
      extension.value !== null ||
      title.value.trim().length > 0 ||
      selectedPositionId.value !== initialPositionId.value ||
      file.value !== null ||
      url.value.trim().length > 0 ||
      selectedPositionIds.value.length > 0 ||
      selectedMemberIds.value.length > 0
  );

  const canSave = computed(() => {
    if (isSubmitting.value) return false;
    if (!contentType.value || !extension.value) return false;
    if (!title.value.trim() || title.value.length > TRAINING_CONTENT_TITLE_MAX_LENGTH) return false;
    if (!responsibleMemberId.value || !selectedPositionId.value) return false;
    if (!hasContent.value) return false;
    if (fileError.value) return false;
    if (!file.value && urlValidation.value.isValid !== true) return false;
    if (!file.value && formatUrl(url.value).length > TRAINING_CONTENT_URL_MAX_LENGTH) return false;
    if (selectedMemberIds.value.length > TRAINING_CONTENT_MAX_VISIBILITY_IDS) return false;
    return selectedMemberIds.value.length > 0;
  });

  const buildPayload = (): CreateTrainingContentPayload => ({
    title: title.value.trim(),
    contentType: contentType.value!,
    extension: extension.value!,
    sourceType: file.value ? 'UPLOAD' : 'URL',
    responsibleMemberId: responsibleMemberId.value!,
    responsiblePositionId: selectedPositionId.value!,
    visibleMemberIds: selectedMemberIds.value
      .map((memberId) => Number(memberId))
      .filter((memberId) => !Number.isNaN(memberId)),
    ...(file.value ? { file: file.value } : { externalUrl: formatUrl(url.value) }),
  });

  const handleUploadProgress = (progressEvent: AxiosProgressEvent) => {
    if (!progressEvent.total) return;
    uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
  };

  const cancelUpload = () => {
    abortController?.abort();
    abortController = null;
    isSubmitting.value = false;
    uploadStatus.value = 'idle';
    uploadProgress.value = 0;
  };

  const submit = async (): Promise<boolean> => {
    if (!canSave.value) return false;

    isSubmitting.value = true;
    abortController = new AbortController();

    if (file.value) {
      uploadStatus.value = 'uploading';
      uploadProgress.value = 0;
    }

    try {
      await trainingContentApi.createTrainingContent(buildPayload(), {
        onUploadProgress: handleUploadProgress,
        signal: abortController.signal,
      });

      if (file.value) {
        uploadProgress.value = 100;
        uploadStatus.value = 'completed';
      }

      showSuccessToast({ title: 'Conteúdo criado com sucesso', type: 'success' });
      return true;
    } catch (error) {
      if (axios.isCancel(error)) {
        return false;
      }

      uploadStatus.value = 'idle';
      showErrorToast(
        {
          title: 'Erro ao criar conteúdo. Tente novamente',
          message: extractApiErrorMessage(error),
          type: 'error',
        },
        { timeout: false }
      );
      return false;
    } finally {
      abortController = null;
      isSubmitting.value = false;
    }
  };

  return {
    contentType,
    extension,
    title,
    responsibleName,
    availablePositions,
    isLoadingPositions,
    hasSinglePosition,
    selectedPositionId,
    createdDate,
    file,
    fileError,
    url,
    urlError,
    visibilityError,
    uploadProgress,
    uploadStatus,
    isUploadDisabled,
    isUrlDisabled,
    isSubmitting,
    isDirty,
    canSave,
    selectedPositionIds,
    selectedMemberIds,
    loadPositions,
    selectFile,
    removeFile,
    cancelUpload,
    submit,
  };
}
